import 'immer';
import { createSlice } from '@reduxjs/toolkit';

import {
  signIn,
  AuthError,
  SignInOutput,
  SignInInput,
  updateUserAttributes,
  getCurrentUser,
  fetchUserAttributes,
  AuthUser,
  signUp,
  SignUpOutput,
  ConfirmSignUpInput,
  ConfirmSignUpOutput,
  confirmSignUp,
  resendSignUpCode,
  ResendSignUpCodeInput,
  ResendSignUpCodeOutput,
  resetPassword,
  ResetPasswordInput,
  ResetPasswordOutput,
  confirmResetPassword,
  ConfirmResetPasswordInput,
  fetchAuthSession,
  AuthSession,
  signOut,
  FetchUserAttributesOutput,
  UpdateUserAttributesOutput,
  UpdateUserAttributesInput,
  updatePassword,
  UpdatePasswordInput,
} from 'aws-amplify/auth';
import type { Dispatch, PayloadAction } from '@reduxjs/toolkit';
import { CredentialManager, client } from '../../util/api-client';
import type { Team } from '@repo/api-contract';
import { RootState } from '../store';
export interface UserState {
  user: 'init' | 'loading' | AuthUser | AuthError;
  session: AuthSession | undefined;
  team: 'init' | 'loading' | Team;
  vanity?: string;
}

const initialState: UserState = {
  user: 'init',
  session: undefined,
  team: 'init',
};

export const userSlice = createSlice({
  name: 'UserSlice',
  initialState,
  reducers: {
    setUser: (
      state,
      action: PayloadAction<'init' | 'loading' | AuthUser | AuthError>,
    ) => ({
      ...state,
      user: action.payload,
    }),
    setVanity: (state, action: PayloadAction<string | undefined>) => ({
      ...state,
      vanity: action.payload,
    }),
    setSession: (state, action: PayloadAction<AuthSession | undefined>) => ({
      ...state,
      session: action.payload,
    }),
    setTeam: (state, action: PayloadAction<Team | 'init' | 'loading'>) => ({
      ...state,
      team: action.payload,
    }),
  },
});

export const { setUser, setVanity, setSession, setTeam } = userSlice.actions;
export default userSlice.reducer;

interface LogInInput extends SignInInput {
  vanity?: string;
}

export const login =
  ({ vanity, username, password }: LogInInput) =>
  async (dispatch: Dispatch): Promise<SignInOutput> => {
    const loginResult = await signIn({
      username,
      password,
    });
    dispatch(setVanity(vanity));

    try {
      dispatch(setUser('loading'));
      const user = await getCurrentUser();
      dispatch(setUser(user));
    } catch (error: unknown) {
      if (error instanceof AuthError) {
        dispatch(setUser(error));
      } else {
        throw error;
      }
    }

    return loginResult;
  };
export const register =
  (username: string, password: string) => async (): Promise<SignUpOutput> => {
    const signUpResult = await signUp({
      username,
      password,
      options: {
        userAttributes: {
          email: username,
        },
      },
    });
    return signUpResult;
  };

export const confirmAccount =
  (input: ConfirmSignUpInput) => async (): Promise<ConfirmSignUpOutput> => {
    const result = await confirmSignUp(input);
    return result;
  };

export const resendConfirmation =
  (input: ResendSignUpCodeInput) =>
  async (): Promise<ResendSignUpCodeOutput> => {
    const result = await resendSignUpCode(input);
    return result;
  };

export const forgotPasswordChallenge =
  (input: ResetPasswordInput) => async (): Promise<ResetPasswordOutput> => {
    const result = resetPassword(input);
    return result;
  };

export const resetPasswordThunk =
  (input: ConfirmResetPasswordInput) => async (): Promise<void> => {
    await confirmResetPassword(input);
  };

const isAuthenticatedUser = (user: UserState['user']): user is AuthUser =>
  user !== 'init' && user !== 'loading' && !(user instanceof AuthError);

export const getUser =
  () =>
  async (
    dispatch: Dispatch,
    getState: () => RootState,
  ): Promise<AuthUser | undefined> => {
    const previousUser = getState().userSlice.user;
    const wasAuthenticated = isAuthenticatedUser(previousUser);

    // Avoid flashing a 'loading' state (which unmounts the app behind a
    // spinner) when we already have a valid user. The re-fetch is just a
    // refresh; keep rendering the existing UI in the meantime.
    if (!wasAuthenticated) {
      dispatch(setUser('loading'));
    }

    try {
      const user = await getCurrentUser();
      const session = await fetchAuthSession();

      dispatch(setUser(user));
      dispatch(setSession(session));
      CredentialManager.setCredentials(
        {
          accessKeyId: session.credentials?.accessKeyId,
          secretAccessKey: session.credentials?.secretAccessKey,
          sessionToken: session.credentials?.sessionToken,
        },
        user.userId,
        user.username,
      );
      return user;
    } catch (error: unknown) {
      if (error instanceof AuthError) {
        dispatch(setUser(error));
        return;
      }

      // Non-auth failure (e.g. network error, Amplify token-rotation race
      // when multiple tabs touch storage at once). If we were already
      // authenticated, keep the existing session so the UI stays usable.
      // Otherwise surface a synthetic AuthError so routing can react instead
      // of leaving the user stuck on a loading spinner forever.
      console.error('[get-user] failed to load user', error);
      if (wasAuthenticated) {
        dispatch(setUser(previousUser));
        return;
      }
      const message =
        error instanceof Error ? error.message : 'Failed to load user';
      dispatch(
        setUser(
          new AuthError({
            name: 'GetCurrentUserFailed',
            message,
            underlyingError: error,
          }),
        ),
      );
    }
  };

export const refreshSession =
  () =>
  async (dispatch: Dispatch): Promise<void> => {
    const session = await fetchAuthSession({ forceRefresh: true });
    if (!session.credentials) {
      // No credentials means the session is gone (e.g. another tab signed
      // out). Treat this as an explicit auth failure so callers can react
      // (e.g. by signing out and redirecting).
      throw new AuthError({
        name: 'NoCredentials',
        message: 'fetchAuthSession returned no credentials',
      });
    }
    dispatch(setSession(session));

    // We already have fresh credentials at this point; if pulling user
    // metadata fails (transient), fall back to whatever we already had so
    // we don't wipe the credential manager.
    let userId: string | undefined;
    let userName: string | undefined;
    try {
      const user = await getCurrentUser();
      userId = user.userId;
      userName = user.username;
    } catch (error) {
      console.warn(
        '[session-refresh] getCurrentUser failed; reusing prior identity',
        error,
      );
      userId = CredentialManager.getUserId();
      userName = CredentialManager.getUserName();
    }

    CredentialManager.setCredentials(
      {
        accessKeyId: session.credentials.accessKeyId,
        secretAccessKey: session.credentials.secretAccessKey,
        sessionToken: session.credentials.sessionToken,
      },
      userId,
      userName,
    );
  };

export const fetchUser = async (): Promise<FetchUserAttributesOutput> => {
  const user = await fetchUserAttributes();
  return user;
};

export const updateUser = async (
  payload: UpdateUserAttributesInput,
): Promise<UpdateUserAttributesOutput> => {
  const user = await updateUserAttributes(payload);
  return user;
};

export const changePassword = async (
  payload: UpdatePasswordInput,
): Promise<void> => {
  await updatePassword(payload);
};

export const logOut =
  () =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      await signOut();
      dispatch(setUser('init'));
      dispatch(setSession(undefined));
      dispatch(setTeam('init'));
      CredentialManager.setCredentials(undefined, undefined, undefined);
      CredentialManager.setTeamId(undefined);
    } catch (error: unknown) {
      if (error instanceof AuthError) {
        dispatch(setUser(error));
      } else {
        throw error;
      }
    }
  };

export const fetchTeam =
  () =>
  async (
    dispatch: Dispatch,
    getState: () => RootState,
  ): Promise<Team | undefined> => {
    const {
      userSlice: { team },
    } = getState();
    if (team === 'loading') return;
    await dispatch(setTeam('loading'));
    const userTeam = await client.getMyTeam.query();
    CredentialManager.setTeamId(userTeam.TeamId);
    await dispatch(setTeam(userTeam));
    return userTeam;
  };
